/**
 * ViewToggle Component - Premium Segmented Control
 * 
 * Replaces the small icon toggle with a proper segmented control.
 * Features:
 * - Rounded container background
 * - Text labels (List | Grid)
 * - Clear active state
 * - Proper touch targets
 * - Smooth transitions
 */

import React, { memo } from 'react';
import { List, LayoutGrid } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './ViewToggle.css';

/**
 * ViewToggle Component
 * 
 * @param {Object} props
 * @param {string} props.view - Current view ('list' or 'grid')
 * @param {Function} props.onViewChange - Callback when view changes
 * @param {boolean} props.disabled - Disable the toggle
 * @param {string} props.size - Size variant ('sm' | 'md' | 'lg')
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showLabels - Show text labels (default: true)
 */
function ViewToggle({
  view = 'grid',
  onViewChange,
  disabled = false,
  size = 'md',
  className = '',
  showLabels = true,
}) {
  const { i18n } = useTranslation();
  const isListView = view === 'list';
  const isGridView = view === 'grid';
  
  // Icon sizes based on variant
  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 18,
  };
  const iconSize = iconSizes[size] || iconSizes.md;

  const handleClick = (newView) => {
    if (disabled || view === newView) return;
    onViewChange?.(newView);
  };

  // Get RTL status reactively from i18n
  const isRTL = i18n.language?.startsWith('ar');
  const listLabel = isRTL ? 'قائمة' : 'List';
  const gridLabel = isRTL ? 'شبكة' : 'Grid';

  return (
    <div 
      className={`view-toggle-segmented view-toggle-${size} ${disabled ? 'view-toggle-disabled' : ''} ${className}`}
      role="group"
      aria-label="View options"
      data-testid="view-toggle"
    >
      {/* Active indicator (animated slider) */}
      <div 
        className={`view-toggle-slider ${isListView ? 'at-list' : 'at-grid'}`}
        aria-hidden="true"
      />
      
      <button
        type="button"
        className={`view-toggle-segment ${isListView ? 'active' : ''}`}
        onClick={() => handleClick('list')}
        disabled={disabled}
        aria-pressed={isListView}
        aria-label="List view"
        data-testid="view-toggle-list"
      >
        <List size={iconSize} />
        {showLabels && <span className="view-toggle-label">{listLabel}</span>}
      </button>
      
      <button
        type="button"
        className={`view-toggle-segment ${isGridView ? 'active' : ''}`}
        onClick={() => handleClick('grid')}
        disabled={disabled}
        aria-pressed={isGridView}
        aria-label="Grid view"
        data-testid="view-toggle-grid"
      >
        <LayoutGrid size={iconSize} />
        {showLabels && <span className="view-toggle-label">{gridLabel}</span>}
      </button>
    </div>
  );
}

export default memo(ViewToggle);
